import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import {
  acceptLegalDocuments,
  fetchLegalStatus,
} from "src/redux/slices/onboarding";
import useAuth from "src/hooks/useAuth";
import { useClaims } from "src/hooks/useClaims";

// Documentos del onboarding — el orden aquí es el orden del wizard.
// Las claves DEBEN coincidir con LegalDocumentKeys del backend.
const DOCUMENTS = [
  {
    key: "data_protection",
    title: "Protección de datos",
    short:
      "Tratamiento de datos personales conforme al RGPD y la LOPDGDD. Como responsable del restaurante eres el responsable del tratamiento de los datos de empleados y clientes.",
    body: `Tratamos los datos personales del personal del restaurante (nombre, DNI, contacto, fichajes), de los clientes (datos de contacto, historial de pedidos, datos de facturación) y de los proveedores con la finalidad de gestionar la operativa del establecimiento.

Como responsable del tratamiento te comprometes a:
  • Mantener actualizada la información de contacto de los interesados.
  • Atender los derechos ARSULIPO (Acceso, Rectificación, Supresión, Limitación, Portabilidad, Oposición) que recibas.
  • Notificar a TakeMyOrder cualquier brecha de seguridad detectada.
  • No usar los datos para finalidades distintas a las pactadas.

TakeMyOrder actúa como encargado del tratamiento — almacenamos y procesamos los datos en tu nombre con las garantías técnicas y organizativas exigidas por el RGPD (cifrado en reposo, backups diarios, auditorías de acceso).`,
  },
  {
    key: "permissions",
    title: "Permisos necesarios",
    short:
      "Concedes a TakeMyOrder los permisos técnicos para emitir facturas, conectar con la AEAT (VeriFactu), enviar SMS/email a tus clientes y procesar pagos en tu nombre.",
    body: `Para que TakeMyOrder funcione correctamente necesitamos los siguientes permisos en tu nombre:

  • Conexión con la AEAT vía VeriFactu para registrar facturas en tiempo real.
  • Emisión de facturas con tus datos fiscales (NIF, razón social, dirección).
  • Envío de comunicaciones transaccionales (recibos, confirmaciones) por SMS y email.
  • Procesamiento de pagos a través de Stripe / TPV virtual asociado a tu cuenta.
  • Almacenamiento de tickets, comandas y arqueos de los últimos 4 años (obligación fiscal).

Puedes revocar parcialmente algunos permisos desde Configuración, pero ten en cuenta que algunas funciones del TPV dejarán de operar (p.ej. sin VeriFactu no podrás emitir facturas conformes).`,
  },
  {
    key: "verifactu",
    title: "VeriFactu y obligaciones fiscales",
    short:
      "Reconoces conocer tus obligaciones fiscales y la normativa VeriFactu del Real Decreto 1007/2023.",
    body: `El Reglamento VeriFactu (RD 1007/2023) obliga a los sistemas informáticos de facturación de empresas y autónomos a remitir cada factura a la AEAT en el momento de su emisión.

Al firmar este apartado declaras que:
  • Eres consciente de tu obligación de emitir factura por todas las operaciones sujetas.
  • TakeMyOrder utilizará la integración VeriFactu para enviar a la AEAT cada factura emitida desde el TPV con los datos que tú hayas configurado.
  • Eres el único responsable de la veracidad de los datos fiscales (NIF, tipo impositivo, regímenes especiales) que configures.
  • TakeMyOrder no se hace responsable de sanciones derivadas de información fiscal incorrecta proporcionada por ti.

Si no firmas este apartado no podrás operar el TPV — la emisión de tickets exige cumplimiento VeriFactu.`,
  },
  {
    key: "responsibility",
    title: "Responsabilidad del titular",
    short:
      "Asumes la responsabilidad como titular del restaurante de los pedidos, cobros, arqueos y comunicaciones generadas desde tu cuenta TakeMyOrder.",
    body: `Como titular de la cuenta TakeMyOrder asumes la responsabilidad de:
  • Las operaciones realizadas por ti y por tus empleados con el TPV.
  • La custodia de las credenciales de acceso (no compartirlas con terceros).
  • La veracidad de los precios, ingredientes, alérgenos y promociones publicados en tu carta.
  • El cumplimiento de la normativa local de apertura, horarios y aforos.
  • La resolución de incidencias con tus clientes (devoluciones, reclamaciones).

TakeMyOrder pone a tu disposición la herramienta y soporte técnico, pero no es responsable del modelo de negocio, decisiones operativas ni de las relaciones con los clientes finales del restaurante.`,
  },
  {
    key: "cookies",
    title: "Cookies y trazas",
    short:
      "Aceptas el uso de cookies técnicas, analíticas y de mejora de producto en el panel TakeMyOrder.",
    body: `TakeMyOrder utiliza cookies y tecnologías similares en el panel de gestión y la web pública del restaurante:

  • Técnicas (necesarias): mantienen tu sesión iniciada, recuerdan preferencias de UI.
  • Analíticas: nos permiten saber qué pantallas se usan más para priorizar mejoras.
  • De producto: detectan errores y rendimiento (Application Insights, Sentry).

No usamos cookies publicitarias ni cedemos datos a terceros con fines de marketing. Puedes gestionar el consentimiento detallado en cualquier momento desde el banner de cookies o la sección "Política de cookies" del menú.`,
  },
];

/**
 * Modal bloqueante: cuando el restaurante tiene documentos legales pendientes
 * de firma, no le dejamos usar la app hasta que recorra el wizard.
 *
 * Sincronización con el slice:
 *   - Al montar (autenticado), llama a fetchLegalStatus.
 *   - Si pending.length > 0 → modal abierto.
 *   - Tras la firma, isCompleted=true → modal se cierra automáticamente.
 *
 * El modal NO tiene cerrar/cancelar — el cierre del navegador es la única salida.
 */
export default function LegalOnboardingGate() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();
  const { isAdmin } = useClaims();
  const { isLoaded, pending, isLoading, isCompleted } = useSelector(
    (s) => s.onboarding,
  );

  // Wizard local state
  const [activeStep, setActiveStep] = useState(0);
  const [acceptedSteps, setAcceptedSteps] = useState({}); // {key: bool}
  const [signerName, setSignerName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Sólo lo dispara para el restaurante (rol USER_NORMAL/USER_ADMIN). Si la
  // cuenta es de admin global o distribuidor el endpoint devolverá vacío de
  // todas formas, pero nos ahorramos la ida si no aplica.
  useEffect(() => {
    if (isAuthenticated && !isLoaded && !isLoading && !isAdmin) {
      dispatch(fetchLegalStatus());
    }
  }, [isAuthenticated, isLoaded, isLoading, dispatch]);

  // Reset interno cuando cambia el set de pendientes (p.ej. tras submit OK).
  useEffect(() => {
    setActiveStep(0);
    setAcceptedSteps({});
  }, [pending.length]);

  if (!isAuthenticated || !isLoaded || isAdmin) return null;
  if (isCompleted || pending.length === 0) return null;

  // Wizard sólo muestra los pendientes (si subió la versión sólo de un doc,
  // re-firmamos sólo ése).
  const steps = DOCUMENTS.filter((d) => pending.includes(d.key));
  // Paso final = paso "firma" — siempre tras los documentos.
  const totalSteps = steps.length + 1; // +1 firma
  const isOnSignature = activeStep === steps.length;
  const currentDoc = isOnSignature ? null : steps[activeStep];

  const isCurrentAccepted = currentDoc ? !!acceptedSteps[currentDoc.key] : true;

  const canSubmitSignature =
    isOnSignature &&
    signerName.trim().length >= 3 &&
    steps.every((s) => acceptedSteps[s.key]);

  const handleNext = () => {
    if (activeStep < totalSteps - 1) setActiveStep(activeStep + 1);
  };
  const handleBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };
  const handleAcceptToggle = (key, value) => {
    setAcceptedSteps((prev) => ({ ...prev, [key]: value }));
  };

  const handleSign = async () => {
    setSubmitting(true);
    try {
      await dispatch(
        acceptLegalDocuments({
          documentKeys: steps.map((s) => s.key),
          signerFullName: signerName.trim(),
        }),
      );
      // Si todo OK, el slice marca isCompleted y el modal se desmonta solo.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      // Sin escape ni clic-fuera — el onboarding es restrictivo.
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Bienvenido a TakeMyOrder
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Antes de empezar, necesitamos que revises y firmes los siguientes
          apartados legales.
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 1, pb: 0 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((s) => (
            <Step key={s.key}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
          <Step>
            <StepLabel>Firma</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <DialogContent dividers sx={{ minHeight: 360 }}>
        {currentDoc ? (
          <>
            <Typography variant="h6" gutterBottom>
              {currentDoc.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentDoc.short}
            </Typography>
            <Box
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: 13,
                lineHeight: 1.6,
                backgroundColor: "background.neutral",
                borderRadius: 1.5,
                p: 2,
                maxHeight: 280,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {currentDoc.body}
            </Box>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={isCurrentAccepted}
                  onChange={(e) =>
                    handleAcceptToggle(currentDoc.key, e.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body2">
                  He leído y acepto el apartado <b>{currentDoc.title}</b>
                </Typography>
              }
            />
          </>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Firma electrónica
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Para confirmar la aceptación de los {steps.length} apartados
              anteriores, escribe tu nombre completo. Se registrará junto con tu
              IP y la fecha como sello de aceptación.
            </Typography>
            <TextField
              fullWidth
              label="Nombre completo del titular o representante legal"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              autoFocus
              error={signerName.length > 0 && signerName.trim().length < 3}
              helperText={
                signerName.length > 0 && signerName.trim().length < 3
                  ? "Introduce al menos 3 caracteres"
                  : `Cuenta: ${user?.email || ""}`
              }
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Resumen de apartados que firmas:
              </Typography>
              <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                {steps.map((s) => (
                  <li key={s.key}>
                    <Typography variant="body2">
                      {s.title}{" "}
                      {acceptedSteps[s.key] ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 0.5 }}
                        >
                          ✓ aceptado
                        </Typography>
                      ) : (
                        <Typography
                          component="span"
                          variant="caption"
                          color="warning.main"
                          sx={{ ml: 0.5 }}
                        >
                          pendiente
                        </Typography>
                      )}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        )}
      </DialogContent>

      {submitting && <LinearProgress />}

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || submitting}>
          Atrás
        </Button>
        <Box sx={{ flex: 1 }} />
        {!isOnSignature && (
          <Button
            variant="contained"
            disabled={!isCurrentAccepted}
            onClick={handleNext}
          >
            Continuar
          </Button>
        )}
        {isOnSignature && (
          <Button
            variant="contained"
            color="success"
            disabled={!canSubmitSignature || submitting}
            onClick={handleSign}
          >
            Firmar y continuar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
